import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  FileVideo,
  CheckCircle2,
  Circle,
  Loader2,
  XCircle,
  ArrowLeft,
  RefreshCw,
  AlertCircle,
} from 'lucide-react'
import { useJobsStore } from '../../stores/jobs'
import { outputs as outputsApi, jobs as jobsApi, type JobOutput, type JobPhase } from '../../lib/api'
import { useJobProgress } from '../../hooks/useJobProgress'
import { OutputCard } from '../OutputViewer/OutputCard'

// ─── Phase Pipeline ───────────────────────────────────────────────────────────

const PHASE_LABELS: Record<string, string> = {
  transcribe: 'Transcribe',
  analyze: 'Analyze',
  generate: 'Generate',
  package: 'Package',
}

function PhaseIcon({ status }: { status: JobPhase['status'] }) {
  if (status === 'done') return <CheckCircle2 size={20} className="text-green-600" />
  if (status === 'active') return <Loader2 size={20} className="animate-spin text-alk-red" />
  if (status === 'failed') return <XCircle size={20} className="text-alk-red" />
  return <Circle size={20} className="text-gray-300" />
}

function PhasePipeline({ phases }: { phases: JobPhase[] }) {
  return (
    <div className="flex items-start gap-0 overflow-x-auto pb-2">
      {phases.map((phase, i) => (
        <div key={phase.name} className="flex items-center">
          <div className="flex flex-col items-center min-w-24 text-center">
            <div
              className={`w-10 h-10 flex items-center justify-center border-2 ${
                phase.status === 'done'
                  ? 'border-green-500 bg-green-50'
                  : phase.status === 'active'
                  ? 'border-alk-red bg-red-50'
                  : phase.status === 'failed'
                  ? 'border-red-400 bg-red-50'
                  : 'border-gray-200 bg-white'
              }`}
            >
              <PhaseIcon status={phase.status} />
            </div>
            <div
              className={`text-xs font-display uppercase tracking-wide mt-1.5 ${
                phase.status === 'active'
                  ? 'text-alk-red font-semibold'
                  : phase.status === 'done'
                  ? 'text-green-600'
                  : 'text-alk-gray'
              }`}
            >
              {PHASE_LABELS[phase.name] ?? phase.label}
            </div>
            {phase.status === 'active' && phase.progress > 0 && (
              <div className="text-xs text-alk-gray mt-0.5">{phase.progress}%</div>
            )}
          </div>
          {i < phases.length - 1 && (
            <div
              className={`h-0.5 w-8 flex-shrink-0 mx-1 mb-6 ${
                phase.status === 'done' ? 'bg-green-400' : 'bg-gray-200'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { currentJob, isLoading, fetchJob, updateJob, clearCurrentJob } = useJobsStore()
  const [jobOutputs, setJobOutputs] = useState<JobOutput[]>([])
  const [loadingOutputs, setLoadingOutputs] = useState(false)

  // SSE progress
  const progress = useJobProgress(
    currentJob?.status === 'processing' || currentJob?.status === 'queued'
      ? (id ?? null)
      : null
  )

  useEffect(() => {
    if (id) void fetchJob(id)
    return () => clearCurrentJob()
  }, [id, fetchJob, clearCurrentJob])

  // When SSE fires, refresh the job data
  useEffect(() => {
    if (progress.isComplete || progress.error) {
      if (id) void fetchJob(id)
    }
  }, [progress.isComplete, progress.error, id, fetchJob])

  // Load outputs when job is complete
  useEffect(() => {
    if (currentJob?.status === 'completed' && id) {
      setLoadingOutputs(true)
      outputsApi.listOutputs(id).then((outs) => {
        setJobOutputs(outs)
        setLoadingOutputs(false)
      }).catch(() => setLoadingOutputs(false))
    }
  }, [currentJob?.status, id])

  const handleCancel = async () => {
    if (!id) return
    try {
      await jobsApi.cancelJob(id)
      void fetchJob(id)
    } catch (err) {
      console.error('Cancel failed', err)
    }
  }

  if (isLoading && !currentJob) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={28} className="animate-spin text-alk-gray" />
      </div>
    )
  }

  if (!currentJob) {
    return (
      <div className="text-center py-20">
        <AlertCircle size={32} className="mx-auto text-alk-gray mb-3" />
        <p className="font-display text-lg uppercase tracking-wide text-alk-black">
          Job not found
        </p>
        <button
          onClick={() => navigate('/dashboard')}
          className="btn-secondary mt-4 flex items-center gap-2 mx-auto"
        >
          <ArrowLeft size={14} /> Dashboard
        </button>
      </div>
    )
  }

  const job = currentJob
  const isActive = job.status === 'processing' || job.status === 'queued'
  const isFailed = job.status === 'failed'
  const isComplete = job.status === 'completed'

  const displayProgress = progress.status ? progress.progress : job.progress

  const createdDate = new Date(job.createdAt).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

  return (
    <div>
      {/* Back */}
      <button
        onClick={() => navigate('/dashboard')}
        className="flex items-center gap-1.5 text-xs text-alk-gray font-display uppercase tracking-wide hover:text-alk-black transition-colors mb-6"
      >
        <ArrowLeft size={13} /> Back to Dashboard
      </button>

      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-alk-black flex items-center justify-center flex-shrink-0">
            <FileVideo size={18} className="text-alk-red" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-2xl uppercase tracking-wide text-alk-black leading-tight truncate">
              {job.title ?? job.filename}
            </h1>
            <p className="text-xs text-alk-gray mt-0.5">{createdDate}</p>
          </div>
        </div>
        {isActive && (
          <button
            onClick={handleCancel}
            className="btn-secondary flex items-center gap-2 flex-shrink-0"
          >
            <XCircle size={14} />
            Cancel
          </button>
        )}
        {isFailed && (
          <button
            onClick={() => id && void fetchJob(id)}
            className="btn-secondary flex items-center gap-2 flex-shrink-0"
          >
            <RefreshCw size={14} />
            Retry
          </button>
        )}
      </div>

      {/* Phase Pipeline */}
      {job.phases && job.phases.length > 0 && (
        <div className="card mb-6">
          <div className="section-label mb-4">Processing Pipeline</div>
          <PhasePipeline phases={job.phases} />

          {/* Overall progress */}
          {isActive && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-alk-gray">
                  {progress.phase
                    ? `${PHASE_LABELS[progress.phase] ?? progress.phase}...`
                    : 'Processing...'}
                </span>
                <span className="text-xs font-semibold text-alk-black">
                  {displayProgress}%
                </span>
              </div>
              <div className="bg-gray-200 h-1.5">
                <div
                  className="bg-alk-red h-full transition-all duration-300"
                  style={{ width: `${displayProgress}%` }}
                />
              </div>
            </div>
          )}

          {isComplete && (
            <div className="mt-4 border-t border-gray-100 pt-4 flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-600" />
              <span className="text-sm text-green-700 font-display uppercase tracking-wide">
                Completed
              </span>
              {job.completedAt && (
                <span className="text-xs text-alk-gray ml-2">
                  {new Date(job.completedAt).toLocaleString()}
                </span>
              )}
            </div>
          )}

          {isFailed && (
            <div className="mt-4 border-t border-gray-100 pt-4">
              <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200">
                <AlertCircle size={14} className="text-alk-red flex-shrink-0" />
                <div>
                  <p className="text-xs font-display uppercase tracking-wide text-alk-red font-semibold">
                    Processing Failed
                  </p>
                  <p className="text-xs text-alk-red mt-0.5">
                    {progress.error ?? job.errorMessage ?? 'An unexpected error occurred.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* File Info */}
      <div className="card mb-6">
        <div className="section-label mb-3">File Information</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs text-alk-gray mb-0.5">Filename</div>
            <div className="font-medium text-alk-black truncate">{job.filename}</div>
          </div>
          <div>
            <div className="text-xs text-alk-gray mb-0.5">Division</div>
            <div className="font-medium text-alk-black">{job.division ?? '—'}</div>
          </div>
          <div>
            <div className="text-xs text-alk-gray mb-0.5">Outputs</div>
            <div className="font-medium text-alk-black">{job.outputs.length} types</div>
          </div>
          <div>
            <div className="text-xs text-alk-gray mb-0.5">Job ID</div>
            <div className="font-mono text-xs text-alk-gray truncate">{job.id}</div>
          </div>
        </div>
        {job.description && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="text-xs text-alk-gray mb-0.5">Description</div>
            <div className="text-sm text-alk-black">{job.description}</div>
          </div>
        )}
      </div>

      {/* Outputs */}
      {isComplete && (
        <div>
          <div className="section-label mb-3">Outputs</div>
          {loadingOutputs ? (
            <div className="flex items-center gap-2 py-8 justify-center">
              <Loader2 size={18} className="animate-spin text-alk-gray" />
              <span className="text-sm text-alk-gray">Loading outputs...</span>
            </div>
          ) : jobOutputs.length === 0 ? (
            <div className="card text-center py-8 text-sm text-alk-gray">
              No outputs found for this job.
            </div>
          ) : (
            <div className="space-y-3">
              {jobOutputs.map((out) => (
                <OutputCard key={out.id} output={out} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Queued state */}
      {job.status === 'queued' && (
        <div className="card flex items-center gap-3 py-6 justify-center">
          <Loader2 size={18} className="animate-spin text-alk-gray" />
          <span className="text-sm text-alk-gray">
            Job is queued and will start processing shortly...
          </span>
        </div>
      )}
    </div>
  )
}
